import apiService from "./api";

export interface SpiritualLine {
	id: string;
	name: string;
	icon?: string;
	primaryColor?: string;
	description?: string;
	createdAt: string;
	updatedAt: string;
}

export interface CreateSpiritualLineData {
	name: string;
	icon?: string;
	primaryColor?: string;
	description?: string;
}

export interface UpdateSpiritualLineData {
	name?: string;
	icon?: string;
	primaryColor?: string;
	description?: string;
}

export interface FindSpiritualLinesParams {
	page?: number;
	limit?: number;
	orderBy?: string;
	order?: "asc" | "desc";
	search?: string;
	name?: string;
	description?: string;
	mode?: "full" | "select";
}

export interface PaginatedSpiritualLines {
	data: SpiritualLine[];
	meta: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

export default {
	/**
	 * Obtém todas as linhas espirituais (sem paginação) com campos enxutos para dropdowns.
	 * Usa o modo `select` do backend, que ignora paginação e retorna apenas id, name, icon, primaryColor.
	 */
	async getAllSpiritualLines(): Promise<SpiritualLine[]> {
		const response = await apiService.get("/spiritual-lines?mode=select");
		return response;
	},

	/**
	 * Obtém linhas espirituais com paginação
	 */
	async getSpiritualLines(
		params?: FindSpiritualLinesParams
	): Promise<PaginatedSpiritualLines> {
		const queryParams = new URLSearchParams();

		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined && value !== null && value !== "") {
					queryParams.append(key, value.toString());
				}
			});
		}

		const url = `/spiritual-lines${
			queryParams.toString() ? `?${queryParams.toString()}` : ""
		}`;
		const response = await apiService.get(url);
		return response;
	},

	/**
	 * Obtém uma linha espiritual específica por ID
	 */
	async getSpiritualLineById(id: string): Promise<SpiritualLine> {
		const response = await apiService.get(`/spiritual-lines/${id}`);
		return response;
	},

	/**
	 * Cria uma nova linha espiritual
	 */
	async createSpiritualLine(
		data: CreateSpiritualLineData
	): Promise<SpiritualLine> {
		const response = await apiService.post("/spiritual-lines", data);
		return response;
	},

	/**
	 * Atualiza uma linha espiritual existente
	 */
	async updateSpiritualLine(
		id: string,
		data: UpdateSpiritualLineData
	): Promise<SpiritualLine> {
		const response = await apiService.patch(`/spiritual-lines/${id}`, data);
		return response;
	},

	/**
	 * Obtém a contagem de itens vinculados a uma linha espiritual
	 * (guias e tipos de ritual). Usado para decidir se a exclusão exige
	 * reatribuição prévia.
	 */
	async getSpiritualLineUsage(
		id: string
	): Promise<{ guides: number; ritualTypes: number }> {
		const response = await apiService.get(`/spiritual-lines/${id}/usage`);
		return response;
	},

	/**
	 * Exclui uma linha espiritual. Se houver guias/tipos de ritual vinculados,
	 * `reassignToId` indica a linha de destino para a qual eles serão movidos
	 * antes da exclusão (reatribuição em massa).
	 */
	async deleteSpiritualLine(id: string, reassignToId?: string): Promise<void> {
		const query = reassignToId
			? `?reassignToId=${encodeURIComponent(reassignToId)}`
			: "";
		await apiService.delete(`/spiritual-lines/${id}${query}`);
	},

	/**
	 * Obtém os guias de uma linha espiritual específica
	 */
	async getSpiritualLineGuides(
		id: string,
		options: {
			activeOnly?: boolean;
			page?: number;
			limit?: number;
			search?: string;
		} = {}
	): Promise<{
		data: any[];
		total: number;
		page: number;
		limit: number;
		totalPages: number;
	}> {
		const { activeOnly = false, page = 1, limit = 10, search } = options;
		const params = new URLSearchParams({
			page: page.toString(),
			limit: limit.toString(),
			activeOnly: activeOnly.toString(),
		});

		if (search) {
			params.append("search", search);
		}

		const response = await apiService.get(
			`/spiritual-lines/${id}/guides?${params.toString()}`
		);
		return response;
	},
};
